import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import { postContactMessage } from '../../api';
import { useLanguage } from '../../i18n/LanguageContext';
import './Contact.css';

function contactFmt(template, vars) {
  return template.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : ''));
}

const Contact = () => {
  const { t } = useLanguage();
  const [nameSurname, setNameSurname] = useState("");
  const [mail, setMail] = useState("");
  const [comment, setComment] = useState("");
  const [phone, setPhone] = useState("");
  const [captchaData, setCaptchaData] = useState({ text: '', answer: '' });
  const [captchaAttempts, setCaptchaAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockUntil, setBlockUntil] = useState(0);
  const [lastSubmission, setLastSubmission] = useState(0);
  const [userCaptchaInput, setUserCaptchaInput] = useState("");

  const generateAdvancedCaptcha = useCallback(() => {
    // Daha karmaşık matematik işlemleri
    const operations = [
      { op: '+', fn: (a, b) => a + b, maxA: 50, maxB: 30 },
      { op: '-', fn: (a, b) => a - b, maxA: 50, maxB: 30 },
      { op: '×', fn: (a, b) => a * b, maxA: 12, maxB: 8 },
      { op: '÷', fn: (a, b) => a * b, maxA: 12, maxB: 8 } // Bölme için çarpım kullan
    ];
    
    const operation = operations[Math.floor(Math.random() * operations.length)];
    const a = Math.floor(Math.random() * operation.maxA) + 1;
    const b = Math.floor(Math.random() * operation.maxB) + 1;
    
    let question, answer;
    
    if (operation.op === '÷') {
      // Bölme için: a × b ÷ b = a
      question = `${a * b} ÷ ${b} = ?`;
      answer = a.toString();
    } else {
      question = `${a} ${operation.op} ${b} = ?`;
      answer = operation.fn(a, b).toString();
    }
    
    return { text: question, answer };
  }, []);

  const validateCaptcha = (input) => {
    // Blok kontrolü
    if (isBlocked && Date.now() < blockUntil) {
      const remainingTime = Math.ceil((blockUntil - Date.now()) / 1000);
      alert(contactFmt(t('contact.alertTooManyTriesWait'), { seconds: remainingTime }));
      return false;
    }

    const now = Date.now();
    if (now - lastSubmission < 30000) {
      alert(t('contact.alertSubmitTooFast'));
      return false;
    }

    if (input.trim() !== captchaData.answer) {
      setCaptchaAttempts((prev) => prev + 1);

      if (captchaAttempts >= 2) {
        setIsBlocked(true);
        setBlockUntil(now + 300000);
        setCaptchaAttempts(0);
        alert(t('contact.alertCaptchaBlocked5Min'));
      } else {
        alert(contactFmt(t('contact.alertWrongCaptcha'), { remaining: 2 - captchaAttempts }));
      }
      return false;
    }
    
    // Başarılı
    setCaptchaAttempts(0);
    return true;
  };

  useEffect(() => {
    setCaptchaData(generateAdvancedCaptcha());
  }, [generateAdvancedCaptcha]);

  const saveComment = async () => {
    if (!validateCaptcha(userCaptchaInput)) {
      return;
    }
    
    // Başarılı gönderim
    setLastSubmission(Date.now());
    
    const data = {
      nameSurname,
      mail,
      phone,
      comment
    }
    
    if (nameSurname.length > 0 && mail.length > 0 && comment.length > 1) {
      const res = await postContactMessage(data);
      if (res.status) {
        alert(t('contact.sendSuccess'));
        setComment("");
        setMail("");
        setPhone("");
        setNameSurname("");
        // Yeni captcha oluştur
        setCaptchaData(generateAdvancedCaptcha());
        setUserCaptchaInput("");
      }
    }
    else {
      alert(t('contact.sendFailed'));
    }
  }

  // Blok süresini kontrol et
  useEffect(() => {
    if (isBlocked && Date.now() >= blockUntil) {
      setIsBlocked(false);
      setBlockUntil(0);
    }
  }, [isBlocked, blockUntil]);

  return (
    <Container className="contact-container py-5">
      <Row>
        <Col md={5}>
          <div className="contact-info">
            <h2>{t('contact.infoTitle')}</h2>
            <div className="info-item">
              <p className="address">
                {t('contact.addressLine1')}
                <br />
                {t('contact.addressLine2')}
              </p>
            </div>
            <div className="info-item">
              <p className="phone">
                <i className="fas fa-phone"></i>
                0 (312) 284 34 18
              </p>
            </div>
            <div className="info-item">
              <p className="email">
                <i className="fas fa-envelope"></i>
                tmkprojesi@akmb.gov.tr
              </p>
            </div>
            <div className="contact-map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3060.8901843262287!2d32.74041277609776!3d39.90131157942792!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14d347862c84861d%3A0x822c27404f3450bf!2sAtat%C3%BCrk%20K%C3%BClt%C3%BCr%2C%20Dil%20ve%20Tarih%20Y%C3%BCksek%20Kurumu!5e0!3m2!1str!2str!4v1710835058040!5m2!1str!2str"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={t('contact.mapIframeTitle')}
              ></iframe>
            </div>
          </div>
        </Col>

        <Col md={7}>
          <div className="contact-form">
            <h3>{t('contact.formTitle')}</h3>
            <Form method="POST">
              <Form.Group className="mb-3">
                <Form.Label>{t('contact.nameLabel')}</Form.Label>
                <Form.Control type="text" onChange={e => setNameSurname(e.target.value)} value={nameSurname} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('contact.emailLabel')}</Form.Label>
                <Form.Control type="email" onChange={e => setMail(e.target.value)} value={mail} placeholder={t('contact.emailPlaceholder')} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('contact.phoneLabel')}</Form.Label>
                <Form.Control type="number" onChange={e => setPhone(e.target.value)} value={phone} placeholder={t('contact.phonePlaceholder')} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('contact.messageLabel')}</Form.Label>
                <Form.Control
                  as="textarea"
                  placeholder={t('contact.messagePlaceholder')}
                  rows={3}
                  onChange={e => setComment(e.target.value)} value={comment}
                />
              </Form.Group>

              <div className="captcha-container">
                <div className="captcha-box">
                  <span>{t('contact.verificationLabel')} </span>
                  <span className="captcha-question">{captchaData.text}</span>
                  <button 
                    type="button" 
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setCaptchaData(generateAdvancedCaptcha())}
                    title={t('contact.captchaRefreshTitle')}
                    disabled={isBlocked}
                  >
                    🔄
                  </button>
                  <Form.Control
                    type="text"
                    placeholder={t('contact.captchaAnswerPlaceholder')}
                    value={userCaptchaInput}
                    onChange={(e) => setUserCaptchaInput(e.target.value)}
                    style={{ width: '150px' }}
                    disabled={isBlocked}
                  />
                </div>
                {isBlocked && (
                  <div className="captcha-blocked">
                    <small className="text-danger">
                      {contactFmt(t('contact.captchaBlockedHint'), {
                        seconds: Math.ceil((blockUntil - Date.now()) / 1000),
                      })}
                    </small>
                  </div>
                )}
              </div>

              <Button
                type="button"
                variant="primary"
                onClick={() => saveComment()}
                className="submit-button mt-3"
                disabled={isBlocked}
              >
                {t('contact.submit')}
              </Button>
            </Form>

          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default Contact; 